import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Phone,
  Plus,
  Edit,
  Trash2,
  X,
  Loader2,
  Users,
  Eye,
  Search,
  Download,
  FileText
} from 'lucide-react';
import {
  createPhoneGroup,
  getPhoneGroups,
  getPhoneGroup,
  updatePhoneGroup,
  deletePhoneGroup,
  exportPhoneGroupToCSV,
  getPhoneGroupsStats,
  getBusinessContacts
} from '../../../redux/api';

const PhoneGroupsView = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    groupNumber: '',
    name: '',
    description: '',
    phoneNumbers: []
  });
  const [phoneInput, setPhoneInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [exportingGroup, setExportingGroup] = useState(null);

  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }
    fetchGroups();
    fetchStats();
  }, [isSuperAdmin]);

  // Refetch available contacts when groups change, editing group changes, or form data changes
  useEffect(() => {
    console.log('🔄 [USE EFFECT] fetchAvailableContacts trigger:', {
      isSuperAdmin,
      loading,
      groupsCount: groups.length,
      editingGroupId: editingGroup?._id,
      formPhonesCount: formData.phoneNumbers.length
    });
    
    // Wait for groups to be loaded before fetching contacts
    if (isSuperAdmin && !loading && groups.length >= 0) {
      console.log('  ✅ Pozivam fetchAvailableContacts');
      fetchAvailableContacts();
    } else {
      console.log('  ⏸️ Preskačem fetchAvailableContacts (loading:', loading, ', groups:', groups.length, ')');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, loading, groups.length, editingGroup?._id, formData.phoneNumbers.length]);

  // Helper function to normalize phone for comparison (remove spaces, dashes, etc.)
  const normalizePhoneForComparison = (phone) => {
    if (!phone) return '';
    // Remove all spaces, dashes, slashes, parentheses, and convert to lowercase
    return phone.replace(/[\s\-\/\(\)]/g, '').toLowerCase();
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await getPhoneGroups();
      const groupsData = response.data?.groups || [];
      setGroups(groupsData);
      
      // Debug: Log all groups and their phones
      console.log('📱 [PHONE GROUPS] Kreirane grupe:', groupsData.length);
      groupsData.forEach((group, groupIdx) => {
        const phones = group.phoneNumbers || [];
        console.log(`  Grupa ${group.groupNumber} (${group.name}):`, {
          totalPhones: group.totalPhones || phones.length || 0,
          groupId: group._id
        });
        console.log(`    Svi telefoni iz Grupe ${group.groupNumber} (${phones.length} ukupno):`);
        phones.forEach((phoneData, phoneIdx) => {
          const normalized = normalizePhoneForComparison(phoneData.phone);
          console.log(`      ${phoneIdx + 1}. ${phoneData.phone} (normalizovan: ${normalized}) - ${phoneData.companyName || 'N/A'} ${phoneData.city ? `(${phoneData.city})` : ''}`);
        });
      });
    } catch (error) {
      console.error('Error fetching phone groups:', error);
      toast.error('Greška pri učitavanju grupa');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await getPhoneGroupsStats();
      setStats(response.data?.stats || null);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchAvailableContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await getBusinessContacts(1, 10000);
      const data = response.data || {};
      const allContacts = data.contacts || [];
      const contactsWithPhone = allContacts.filter(contact => 
        (contact.mobilePhone && contact.mobilePhone.trim() !== '') ||
        (contact.fixedPhone && contact.fixedPhone.trim() !== '')
      );
      
      // Get all phone numbers that are already assigned to groups
      // Exclude phones from the currently edited group (if editing)
      // Normalize phones for comparison (remove formatting differences)
      const assignedPhones = new Set();
      
      console.log('🔍 [FETCH CONTACTS] Počinje fetchAvailableContacts');
      console.log('  - Groups count:', groups.length);
      console.log('  - Form phones count:', formData.phoneNumbers?.length || 0);
      
      // Add phones from saved groups
      groups.forEach((group, groupIdx) => {
        // If editing a group, exclude phones from that group so they can be edited
        if (editingGroup && group._id === editingGroup._id) {
          console.log(`  - Preskačem Grupu ${group.groupNumber} (editing)`);
          return; // Skip this group's phones
        }
        const phones = group.phoneNumbers || [];
        console.log(`  - Grupa ${group.groupNumber}: ${phones.length} telefona`);
        phones.forEach((phoneData, phoneIdx) => {
          if (phoneData.phone) {
            const normalized = normalizePhoneForComparison(phoneData.phone);
            if (normalized) {
              assignedPhones.add(normalized);
              if (phoneIdx < 3) { // Log first 3 phones from each group
                console.log(`    ${phoneIdx + 1}. ${phoneData.phone} -> ${normalized}`);
              }
            }
          }
        });
      });
      
      // CRITICAL: Also add phones from current form (formData.phoneNumbers)
      // This prevents showing phones that are already in the form but not yet saved
      console.log('  - Dodajem telefone iz forme:', formData.phoneNumbers?.length || 0);
      formData.phoneNumbers?.forEach((phoneData, phoneIdx) => {
        if (phoneData.phone) {
          const normalized = normalizePhoneForComparison(phoneData.phone);
          if (normalized) {
            assignedPhones.add(normalized);
            if (phoneIdx < 3) { // Log first 3 phones from form
              console.log(`    ${phoneIdx + 1}. ${phoneData.phone} -> ${normalized}`);
            }
          }
        }
      });
      
      console.log('  - Ukupno dodeljenih telefona nakon dodavanja:', assignedPhones.size);
      
      // Filter out contacts whose phones are already assigned to groups
      // Compare normalized versions to handle format differences
      // CRITICAL: Contact is available ONLY if NONE of its phones are assigned
      const availableContacts = contactsWithPhone.filter(contact => {
        const mobilePhone = contact.mobilePhone ? normalizePhoneForComparison(contact.mobilePhone) : null;
        const fixedPhone = contact.fixedPhone ? normalizePhoneForComparison(contact.fixedPhone) : null;
        
        // Check if mobile phone is assigned
        const mobileAssigned = mobilePhone && assignedPhones.has(mobilePhone);
        // Check if fixed phone is assigned
        const fixedAssigned = fixedPhone && assignedPhones.has(fixedPhone);
        
        // Contact is available ONLY if NEITHER phone is assigned
        // If contact has no phones, exclude it
        if (!mobilePhone && !fixedPhone) {
          return false;
        }
        
        // Contact is available if no phones are assigned
        return !mobileAssigned && !fixedAssigned;
      });
      
      // Sort contacts by city (alphabetically, starting from A)
      availableContacts.sort((a, b) => {
        const cityA = (a.city || '').trim().toLowerCase();
        const cityB = (b.city || '').trim().toLowerCase();
        if (cityA < cityB) return -1;
        if (cityA > cityB) return 1;
        // If cities are the same, sort by company name
        const companyA = (a.companyName || '').trim().toLowerCase();
        const companyB = (b.companyName || '').trim().toLowerCase();
        return companyA.localeCompare(companyB);
      });
      
      // Debug: Log first 10 contacts from total list and check for conflicts
      console.log('📋 [AVAILABLE CONTACTS] Prvih 10 kontakata iz total liste:');
      availableContacts.slice(0, 10).forEach((contact, idx) => {
        const phone = contact.mobilePhone || contact.fixedPhone;
        const normalizedPhone = phone ? normalizePhoneForComparison(phone) : '';
        const isAssigned = assignedPhones.has(normalizedPhone);
        console.log(`  ${idx + 1}. ${contact.companyName} - ${phone} (${contact.city || 'N/A'})`, {
          normalized: normalizedPhone,
          isAssigned: isAssigned ? '❌ KONFLIKT!' : '✅ OK',
          originalMobile: contact.mobilePhone,
          originalFixed: contact.fixedPhone
        });
        
        // Check if this phone exists in assignedPhones with different normalization
        if (isAssigned) {
          console.log(`    ⚠️ KONFLIKT DETEKTOVAN! Tražim u assignedPhones:`);
          console.log(`      - Normalizovan telefon: "${normalizedPhone}"`);
          console.log(`      - Postoji u assignedPhones: ${assignedPhones.has(normalizedPhone)}`);
          // Check all variations
          const variations = [
            normalizedPhone,
            phone?.replace(/[\s\-\/\(\)]/g, '').toLowerCase(),
            phone?.replace(/[\s\-\/\(\)]/g, ''),
            phone?.toLowerCase(),
            phone
          ];
          variations.forEach((variant, vIdx) => {
            if (variant && assignedPhones.has(variant)) {
              console.log(`      - Varijacija ${vIdx + 1} "${variant}" PRONAĐENA u assignedPhones!`);
            }
          });
        }
      });
      
      console.log('📊 [ASSIGNED PHONES] Ukupno dodeljenih telefona:', assignedPhones.size);
      console.log('  Prvih 20 dodeljenih telefona (normalizovani):');
      Array.from(assignedPhones).slice(0, 20).forEach((phone, idx) => {
        console.log(`    ${idx + 1}. ${phone}`);
      });
      
      // Check for specific phones that are showing as conflicts
      const conflictPhones = ['+381638005740', '+381631615749', '+381666536530'];
      console.log('🔍 [CONFLICT CHECK] Provera specifičnih telefona:');
      conflictPhones.forEach(phone => {
        const normalized = normalizePhoneForComparison(phone);
        console.log(`  ${phone} -> normalizovan: "${normalized}" -> postoji: ${assignedPhones.has(normalized)}`);
      });
      
      setAvailableContacts(availableContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      const response = await getPhoneGroup(groupId);
      setSelectedGroup(response.data?.group || null);
    } catch (error) {
      console.error('Error fetching group details:', error);
      toast.error('Greška pri učitavanju detalja grupe');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addPhoneNumber = () => {
    if (!phoneInput.trim()) return;
    
    const phones = phoneInput.split(/[,\n]/).map(p => p.trim()).filter(p => p);
    
    // Check for duplicates and phones already assigned to groups
    const validPhones = [];
    const invalidPhones = [];
    
    phones.forEach(phone => {
      const normalizedPhone = phone.trim();
      const normalizedForComparison = normalizePhoneForComparison(normalizedPhone);
      
      // Check if already in form (using normalized comparison)
      const existsInForm = formData.phoneNumbers.some(p => 
        normalizePhoneForComparison(p.phone) === normalizedForComparison
      );
      
      if (existsInForm) {
        invalidPhones.push(`${phone} (već postoji u grupi)`);
        return;
      }
      
      // Check if assigned to another group (using normalized comparison)
      if (!editingGroup) {
        // Creating new group - check all groups
        const assignedToGroup = groups.some(group => 
          group.phoneNumbers?.some(phoneData => {
            const assignedNormalized = normalizePhoneForComparison(phoneData.phone);
            return assignedNormalized === normalizedForComparison;
          })
        );
        
        if (assignedToGroup) {
          invalidPhones.push(`${phone} (već dodeljen drugoj grupi)`);
          return;
        }
      } else {
        // Editing group - check other groups
        const assignedToOtherGroup = groups.some(group => 
          group._id !== editingGroup._id &&
          group.phoneNumbers?.some(phoneData => {
            const assignedNormalized = normalizePhoneForComparison(phoneData.phone);
            return assignedNormalized === normalizedForComparison;
          })
        );
        
        if (assignedToOtherGroup) {
          invalidPhones.push(`${phone} (već dodeljen drugoj grupi)`);
          return;
        }
      }
      
      validPhones.push({
        phone: normalizedPhone,
        companyName: null
      });
    });
    
    if (invalidPhones.length > 0) {
      toast.warning(`Neki telefoni nisu dodati:\n${invalidPhones.join('\n')}`);
    }
    
    if (validPhones.length > 0) {
      if (formData.phoneNumbers.length + validPhones.length > 100) {
        toast.error('Maksimum 100 telefona po grupi');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        phoneNumbers: [...prev.phoneNumbers, ...validPhones]
      }));
      
      if (validPhones.length > 0) {
        toast.success(`${validPhones.length} telefona dodato`);
      }
    }
    
    setPhoneInput('');
  };

  const removePhoneNumber = (index) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index)
    }));
  };

  const addContactFromList = (contact) => {
    // Use mobile phone if available, otherwise fixed phone
    const phone = contact.mobilePhone || contact.fixedPhone;
    if (!phone || phone.trim() === '') {
      toast.warning('Kontakt nema broj telefona');
      return;
    }

    const normalizedPhone = normalizePhoneForComparison(phone);

    // Check if phone already exists in current form (using normalized comparison)
    const phoneExistsInForm = formData.phoneNumbers.some(p => 
      normalizePhoneForComparison(p.phone) === normalizedPhone
    );
    
    if (phoneExistsInForm) {
      toast.warning('Broj telefona već postoji u grupi');
      return;
    }

    // Check if phone is already assigned to another group (when creating new group)
    if (!editingGroup) {
      const phoneAssignedToOtherGroup = groups.some(group => 
        group.phoneNumbers?.some(phoneData => {
          const assignedNormalized = normalizePhoneForComparison(phoneData.phone);
          return assignedNormalized === normalizedPhone;
        })
      );
      
      if (phoneAssignedToOtherGroup) {
        toast.warning('Broj telefona je već dodeljen drugoj grupi');
        return;
      }
    } else {
      // When editing, check if phone is in another group (not the one being edited)
      const phoneAssignedToOtherGroup = groups.some(group => 
        group._id !== editingGroup._id &&
        group.phoneNumbers?.some(phoneData => {
          const assignedNormalized = normalizePhoneForComparison(phoneData.phone);
          return assignedNormalized === normalizedPhone;
        })
      );
      
      if (phoneAssignedToOtherGroup) {
        toast.warning('Broj telefona je već dodeljen drugoj grupi');
        return;
      }
    }

    if (formData.phoneNumbers.length >= 100) {
      toast.error('Maksimum 100 telefona po grupi');
      return;
    }

    setFormData(prev => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, {
        phone: phone.trim(), // Keep original format, backend will normalize
        companyName: contact.companyName,
        city: contact.city || null
      }]
    }));
  };

  const resetForm = () => {
    setFormData({
      groupNumber: '',
      name: '',
      description: '',
      phoneNumbers: []
    });
    setPhoneInput('');
    setEditingGroup(null);
    setShowForm(false);
    // Refetch available contacts when form is closed
    setTimeout(() => {
      fetchAvailableContacts();
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.groupNumber) {
      toast.error('Broj grupe je obavezan');
      return;
    }

    if (formData.phoneNumbers.length === 0) {
      toast.error('Dodajte barem jedan broj telefona');
      return;
    }

    if (formData.phoneNumbers.length > 100) {
      toast.error('Maksimum 100 telefona po grupi');
      return;
    }

    setSubmitting(true);
    try {
      if (editingGroup) {
        await updatePhoneGroup({ id: editingGroup._id, groupData: formData });
        toast.success('Grupa je uspešno ažurirana');
      } else {
        await createPhoneGroup(formData);
        toast.success('Grupa je uspešno kreirana');
      }
      resetForm();
      fetchGroups();
      fetchStats();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju grupe');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      groupNumber: group.groupNumber,
      name: group.name || `Grupa ${group.groupNumber}`,
      description: group.description || '',
      phoneNumbers: group.phoneNumbers || []
    });
    setShowForm(true);
    // Refetch available contacts to exclude phones from this group
    setTimeout(() => {
      fetchAvailableContacts();
    }, 100);
  };

  const handleDelete = async (groupId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovu grupu?')) {
      return;
    }

    try {
      await deletePhoneGroup(groupId);
      toast.success('Grupa je uspešno obrisana');
      fetchGroups();
      fetchStats();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.response?.data?.message || 'Greška pri brisanju grupe');
    }
  };

  const handleExport = async (groupId) => {
    try {
      setExportingGroup(groupId);
      const response = await exportPhoneGroupToCSV(groupId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `spinTasker-grupa-${groupId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Grupa je uspešno izvezena');
      fetchGroups();
      fetchStats();
    } catch (error) {
      console.error('Error exporting group:', error);
      toast.error(error.response?.data?.message || 'Greška pri izvozu grupe');
    } finally {
      setExportingGroup(null);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Nacrt',
      ready: 'Spremno',
      exported: 'Izvezeno'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-gray-400',
      ready: 'text-blue-400',
      exported: 'text-green-400'
    };
    return colors[status] || 'text-gray-400';
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = !searchTerm || 
      group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.groupNumber.toString().includes(searchTerm);
    const matchesStatus = !filterStatus || group.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Phone className="h-6 w-6" />
            Mobilni Telefoni
          </h1>
          <p className="text-gray-400 mt-1">Upravljanje statičnim grupama mobilnih telefona za Viber</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-4 w-4" />
          Nova Grupa
        </button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Ukupno Grupa</p>
                <p className="text-2xl font-bold text-white">{stats.totalGroups}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Ukupno Telefona</p>
                <p className="text-2xl font-bold text-white">{stats.totalPhones}</p>
              </div>
              <Phone className="h-8 w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Izvezeno Grupa</p>
                <p className="text-2xl font-bold text-white">{stats.totalExported}</p>
              </div>
              <Download className="h-8 w-8 text-yellow-400" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Spremno</p>
                <p className="text-2xl font-bold text-white">{stats.groupsByStatus?.ready || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Pretraži grupe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
        >
          <option value="">Svi statusi</option>
          <option value="draft">Nacrt</option>
          <option value="ready">Spremno</option>
          <option value="exported">Izvezeno</option>
        </select>
      </div>

      {/* Groups List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Phone className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nema grupa. Kreirajte novu grupu da počnete.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <div key={group._id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-white">{group.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(group.status)}`}>
                      {getStatusLabel(group.status)}
                    </span>
                  </div>
                  {group.description && (
                    <p className="text-gray-400 text-sm mb-3">{group.description}</p>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <p className="text-gray-400 text-xs">Telefona</p>
                      <p className="text-white font-semibold">{group.totalPhones || group.phoneNumbers?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Status</p>
                      <p className="text-white font-semibold">{getStatusLabel(group.status)}</p>
                    </div>
                    {group.exportedAt && (
                      <div>
                        <p className="text-gray-400 text-xs">Izvezeno</p>
                        <p className="text-white font-semibold text-sm">
                          {new Date(group.exportedAt).toLocaleDateString('sr-RS')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => fetchGroupDetails(group._id)}
                    className="p-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition"
                    title="Detalji"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(group)}
                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    title="Izmeni"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {/* Delete button - show if not exported */}
                  {!group.exportedAt && (
                    <button
                      onClick={() => handleDelete(group._id)}
                      className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                      title="Obriši"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  {/* Export button */}
                  {group.phoneNumbers && group.phoneNumbers.length > 0 && (
                    <button
                      onClick={() => handleExport(group._id)}
                      disabled={exportingGroup === group._id}
                      className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                      title="Izvezi u Excel"
                    >
                      {exportingGroup === group._id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Group Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingGroup ? 'Izmeni Grupu' : 'Nova Grupa'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Broj Grupe *
                </label>
                <input
                  type="number"
                  name="groupNumber"
                  value={formData.groupNumber}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingGroup}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Naziv
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={`Grupa ${formData.groupNumber || ''}`}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Opis
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Brojevi Telefona ({formData.phoneNumbers.length}/100)
                </label>
                <div className="flex gap-2 mb-2">
                  <textarea
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="Unesite brojeve telefona (jedan po liniji ili odvojeni zarezom)"
                    rows={3}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addPhoneNumber}
                    disabled={!phoneInput.trim() || formData.phoneNumbers.length >= 100}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    Dodaj
                  </button>
                </div>
                {availableContacts.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-sm text-gray-400 cursor-pointer hover:text-white">
                      Dodaj iz liste kontakata ({availableContacts.length} dostupno)
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 rounded-lg p-2">
                      {availableContacts
                        .filter(contact => {
                          const phone = contact.mobilePhone || contact.fixedPhone;
                          if (!phone) return false;
                          const normalizedPhone = normalizePhoneForComparison(phone);
                          return !formData.phoneNumbers.some(p => 
                            normalizePhoneForComparison(p.phone) === normalizedPhone
                          );
                        })
                        .slice(0, 50)
                        .map((contact) => (
                          <button
                            key={contact._id}
                            type="button"
                            onClick={() => addContactFromList(contact)}
                            disabled={formData.phoneNumbers.length >= 100}
                            className="block w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 rounded disabled:opacity-50"
                          >
                            {contact.companyName} - {contact.mobilePhone || contact.fixedPhone}{contact.city ? ` (${contact.city})` : ''}
                          </button>
                        ))}
                    </div>
                  </details>
                )}
                <div className="mt-2 max-h-40 overflow-y-auto bg-gray-700 rounded-lg p-2">
                  {formData.phoneNumbers.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-2">Nema brojeva telefona</p>
                  ) : (
                    formData.phoneNumbers.map((phoneData, index) => (
                      <div key={index} className="flex items-center justify-between px-3 py-2 hover:bg-gray-600 rounded">
                        <span className="text-sm text-white">
                          {phoneData.companyName ? `${phoneData.companyName} - ` : ''}
                          {phoneData.phone}
                          {phoneData.city ? ` (${phoneData.city})` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => removePhoneNumber(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Otkaži
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    editingGroup ? 'Sačuvaj' : 'Kreiraj'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      {selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Detalji Grupe - {selectedGroup.name}</h2>
              <button
                onClick={() => setSelectedGroup(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Telefona</p>
                  <p className="text-white font-bold text-xl">{selectedGroup.totalPhones || selectedGroup.phoneNumbers?.length || 0}</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-400 text-xs">Status</p>
                  <p className="text-white font-bold text-xl">{getStatusLabel(selectedGroup.status)}</p>
                </div>
                {selectedGroup.exportedAt && (
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-gray-400 text-xs">Izvezeno</p>
                    <p className="text-white font-bold text-xl">
                      {new Date(selectedGroup.exportedAt).toLocaleDateString('sr-RS')}
                    </p>
                  </div>
                )}
              </div>

              {/* Phone Numbers */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Brojevi Telefona</h3>
                <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {selectedGroup.phoneNumbers?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedGroup.phoneNumbers.map((phoneData, index) => (
                        <div key={index} className="text-sm text-white">
                          {phoneData.companyName ? `${phoneData.companyName} - ` : ''}
                          {phoneData.phone}
                          {phoneData.city ? ` (${phoneData.city})` : ''}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Nema brojeva telefona</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneGroupsView;
